import { resolveProgressTexts } from '@/components/Functional/utils/rewardCondition'
import { buildRewardConditionTree } from '@/components/Functional/utils/rewardConditionTree'
import { RewardConditionTreeView } from '@/components/Models/reward/RewardConditionTreeView'
import { Typography } from '@/components/UI/Typography'
import type { RewardOverviewData } from '@/schemas/v2/reward'
import { IconChevronRight } from '@tabler/icons-react'
import Link from 'next/link'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { RewardAcquisitionStatusBadge } from '../RewardAcquisitionStatusBadge'

export type { RewardOverviewData }

type Props = {
  reward: RewardOverviewData
  /** 特典詳細ページへのパス（例: `/uji/rewards/detail?query=1`） */
  href: string
}

export function RewardOverview({ reward, href }: Props) {
  const { t } = useTranslation()

  // 入れ子のグループがある特典だけツリーにする。
  //
  // ツリーのノードは Mantine が onClick で stopPropagation するため、
  // 全面リンク（下の absolute inset-0）にクリックが届かない。
  // グループが無い特典まで常にツリーにすると、条件テキストの領域が
  // 「押しても何も起きない死角」になり、カード全体が遷移するという挙動が壊れる。
  // 構造を示す必要が無い特典では従来どおりのフラットテキストに戻し、
  // カード1枚ごとに Tree を積む描画コスト（useTree のコントローラ・
  // document への clickOutside リスナー・余分なタブストップ）も払わない。
  const hasConditionGroups = reward.conditionGroups.length > 0

  // Mantine Tree は data の参照が変わるたび initialize() を呼ぶため、必ず memo 化する。
  // reward は buildDisplayRewards() が useMemo で生成しており参照が安定している。
  const conditionTree = useMemo(
    () => (hasConditionGroups ? buildRewardConditionTree(reward.conditions, reward.conditionGroups) : []),
    [hasConditionGroups, reward.conditions, reward.conditionGroups],
  )
  const hasConditionTree = conditionTree.length > 0

  const conditionTexts = hasConditionTree ? [] : resolveProgressTexts(t, reward.conditions)

  return (
    // 余白は 8px グリッド（左右16px / 上下16px / サムネイルとの間16px）。
    // min-h はサムネイル 100px + 上下 padding 16px × 2 = 132px で、スポット一覧カードと行高を揃える。
    //
    // カード全体を <Link> で包まず、カード全面を覆う透明なリンクを重ねる（stretched link）。
    // 獲得条件ツリーは開閉できるノード（role=treeitem / onClick）を含むため、
    // <a> の内側に入れるとノードのタップで詳細へ遷移してしまい、
    // インタラクティブ要素を a で包む形になり a11y 上も不正になる。
    // ツリーだけを z-index でリンクより前面に出すことで、
    // 「カードのどこを押しても遷移する」挙動を保ったままツリーを操作できる。
    <div className="relative flex min-h-[132px] items-start gap-4 px-4 py-4">
      {/* サムネイル */}
      {reward.image ? (
        <img src={reward.image} alt={reward.name ?? ''} className="size-[100px] shrink-0 rounded-[16px] object-cover" />
      ) : (
        <div className="size-[100px] shrink-0 rounded-[16px] bg-gray-100" aria-hidden="true" />
      )}

      {/* テキスト情報。特典名・状態ラベル・条件テキストの縦間隔は gap:4px に一元管理し、
          子要素に margin を持たせない */}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {/* 特典名: 16px / Bold / 24px。スポット一覧カードのスポット名と同じ階層に揃える。
            テキスト列の 4px リズムを保つため、リンクで包まずこの階層の直下に置く */}
        <Typography variant="subtitle1" component="p" c="#000000" className="line-clamp-2">
          {reward.name}
        </Typography>
        {/* 獲得状況バッジ。inline-block を保つため div でラップする（flex 直下だと横幅が伸びる） */}
        {reward.acquisitionStatus && (
          <div>
            <RewardAcquisitionStatusBadge status={reward.acquisitionStatus} />
          </div>
        )}
        {/* 条件テキストが複数行のときも同じ 4px 間隔で積む（ツリー表示時は出さない） */}
        {conditionTexts.length > 0 && (
          <div className="flex flex-col gap-1">
            {conditionTexts.map((text, i) => (
              <Typography key={`${i}-${text}`} variant="caption1" component="p" className="text-gray-500">
                {text}
              </Typography>
            ))}
          </div>
        )}
        {/* 獲得条件ツリー（#3581）。従来の条件テキストと同じ位置（サムネイル横のテキスト列）に置く。
            z-10 で全面リンクより前面に出し、ノードのタップが遷移にならないようにする。 */}
        {hasConditionTree && (
          <div className="relative z-10">
            {/* 初期状態はすべて折りたたみ（RewardConditionTreeView の既定）。
                カードが縦に伸びるとリストがスクロールしづらくなるため。 */}
            <RewardConditionTreeView key={reward.id} nodes={conditionTree} />
          </div>
        )}
      </div>

      {/* 矢印 */}
      <IconChevronRight size={24} stroke={1.5} className="shrink-0 self-center text-gray-400" aria-hidden="true" />

      {/* カード全面を覆う透明なリンク。
          absolute のため in-flow なテキスト・画像より前面に描画され、カードのどこを押しても遷移する。
          獲得条件ツリーだけは z-10 でこのリンクより前面に出している。
          読み上げ名は aria-label で与える（子に特典名のテキストノードを増やすと、
          画面上の特典名と二重になりテキスト検索が曖昧になるため）。
          特典名が無い場合に空の aria-label を渡すとリンクの読み上げ名が消えるため、
          遷移先を表す汎用文言にフォールバックする。 */}
      <Link
        href={href}
        aria-label={reward.name ?? t('reward_detail.dialog_title', '特典詳細画面')}
        className="absolute inset-0 z-0"
      />
    </div>
  )
}
